import { useCallback, useState } from 'react';

export type LoadBuildingAccordionDefaultView = 'expanded' | 'collapsed';

export const LOAD_BUILDING_ACCORDION_DEFAULT_VIEW_KEY =
  'drumkit-load-building-accordion-default-view';

/** Matches current ship behavior until product opts into collapsed-by-default. */
export const LOAD_BUILDING_ACCORDION_DEFAULT_VIEW: LoadBuildingAccordionDefaultView =
  'expanded';

export function getLoadBuildingAccordionDefaultView(): LoadBuildingAccordionDefaultView {
  try {
    const stored = localStorage.getItem(
      LOAD_BUILDING_ACCORDION_DEFAULT_VIEW_KEY
    );
    if (stored === 'expanded' || stored === 'collapsed') {
      return stored;
    }
  } catch {
    // localStorage may be unavailable (private mode / sandbox)
  }
  return LOAD_BUILDING_ACCORDION_DEFAULT_VIEW;
}

export function setLoadBuildingAccordionDefaultView(
  view: LoadBuildingAccordionDefaultView
): void {
  try {
    localStorage.setItem(LOAD_BUILDING_ACCORDION_DEFAULT_VIEW_KEY, view);
  } catch {
    // Ignore write failures; in-memory preference still applies for the session
  }
}

type UseLoadBuildingAccordionTabsOptions = {
  /** Every accordion section value (including nested pickup/consignee). */
  allTabs: string[];
  /**
   * Tabs to open when default view is "expanded".
   * Defaults to `allTabs`. Forms may omit optional sections (e.g. billTo).
   */
  expandedTabs?: string[];
};

/**
 * Controlled accordion tabs for load builders with Expand/Collapse all and a
 * persisted default-view preference (ENG-7579).
 */
export function useLoadBuildingAccordionTabs({
  allTabs,
  expandedTabs,
}: UseLoadBuildingAccordionTabsOptions) {
  const tabsWhenExpanded = expandedTabs ?? allTabs;

  const [defaultView, setDefaultViewState] =
    useState<LoadBuildingAccordionDefaultView>(
      getLoadBuildingAccordionDefaultView
    );

  const [activeTabs, setActiveTabs] = useState<string[]>(() =>
    getLoadBuildingAccordionDefaultView() === 'collapsed'
      ? []
      : tabsWhenExpanded
  );

  const expandAll = useCallback(() => {
    setActiveTabs(allTabs);
  }, [allTabs]);

  const collapseAll = useCallback(() => {
    setActiveTabs([]);
  }, []);

  const setDefaultView = useCallback(
    (view: LoadBuildingAccordionDefaultView) => {
      setLoadBuildingAccordionDefaultView(view);
      setDefaultViewState(view);
      setActiveTabs(view === 'collapsed' ? [] : tabsWhenExpanded);
    },
    [tabsWhenExpanded]
  );

  return {
    activeTabs,
    setActiveTabs,
    expandAll,
    collapseAll,
    defaultView,
    setDefaultView,
  };
}
